import React, { useState } from 'react';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { CheckCircle, Send, MapPin, Phone, Mail } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

// Backends can take a while to wake up from a cold start — don't make the
// visitor wait more than this for a response before showing success anyway.
const SUBMIT_TIMEOUT_MS = 4000;

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const sectionRef = useScrollReveal('.contact-reveal', { y: 32, stagger: 0.15 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const contactPromise = submitContact({ ...form, page: 'homepage_contact_section' });
    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    if (!result.timedOut && result.error) console.error(result.error);
    setSent(true);
    setForm({ name: '', email: '', phone: '', message: '' });
    setLoading(false);
  };

  return (
    <section ref={sectionRef} id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-14">
          <div className="contact-reveal">
            <p className="font-body text-sm font-semibold text-[#0056D2] uppercase tracking-wider mb-2">Contact Us</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#1F1F1F] mb-4">
              Start Your Learning Journey
            </h2>
            <p className="font-body text-base text-[#585858] leading-relaxed mb-8 max-w-md">
              Fill out the form and our team will get back to you within 24 hours with a personalized learning path.
            </p>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#E8F0FE] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#0056D2]" />
                </div>
                <div>
                  <p className="font-body text-xs text-[#585858]">Email</p>
                  <p className="font-heading font-semibold text-sm text-[#1F1F1F]">info@a7globetech.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#E8F0FE] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#0056D2]" />
                </div>
                <div>
                  <p className="font-body text-xs text-[#585858]">Phone</p>
                  <p className="font-heading font-semibold text-sm text-[#1F1F1F]">+91 90328 09977</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#E8F0FE] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#0056D2]" />
                </div>
                <div>
                  <p className="font-body text-xs text-[#585858]">Location</p>
                  <p className="font-body text-sm text-black/60">
                Flat No 501, Navya Gardenia, Phase-III,<br />
                Kamalapuri Colony, Banjara Hills,<br />
                Sagar Society, Srinagar Colony,<br />
                Hyderabad, India 500073
              </p>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-reveal">
            {sent ? (
              <div className="flex flex-col items-center justify-center h-full py-14 text-center bg-white rounded-lg border border-[#E1E7EF] shadow-sm" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-[#E8F0FE] flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-[#0056D2]" />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#1F1F1F]">Message Sent!</h3>
                <p className="font-body text-sm text-[#585858] mt-2">We'll get back to you shortly.</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 font-body font-semibold text-sm bg-[#0056D2] text-white px-6 py-2.5 rounded-md hover:bg-[#003E99] transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-lg border border-[#E1E7EF] p-7 shadow-sm" data-testid="contact-form">
                <div>
                  <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Full Name</label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md h-11"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Email</label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md h-11"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Phone</label>
                  <Input
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md h-11"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Message</label>
                  <Textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    required
                    rows={4}
                    className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md resize-none"
                    placeholder="Let us know which course you are interested in"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full font-body font-semibold text-sm bg-[#0056D2] text-white py-3 rounded-md hover:bg-[#003E99] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
